import React, { useState } from 'react';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (value, hide) => {
    if (hide) return '$***';
    if (value == null) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const fmtShort = (value, hide) => {
    if (hide) return '$***';
    if (value == null) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// Priority order per account type (first = most preferred)
const PRIORITY = {
    non_taxable:  ['stocks', 'indexes', 'bonds', 'mixed'],
    tax_deferred: ['stocks', 'indexes', 'bonds', 'mixed'],
    taxable:      ['bonds',  'indexes', 'stocks', 'mixed']
};

const ACCOUNT_TYPE_LABEL = {
    non_taxable:  'Non-Taxable',
    tax_deferred: 'Tax-Deferred',
    taxable:      'Taxable'
};

const ACCOUNT_TYPE_BADGE = {
    non_taxable:  'bg-success',
    tax_deferred: 'bg-info text-dark',
    taxable:      'bg-warning text-dark'
};

const SEC_TYPE_BADGE = {
    stocks:  '#007bff',
    indexes: '#6f42c1',
    bonds:   '#28a745',
    mixed:   '#fd7e14',
    cash:    '#6c757d'
};

// ─── insight generator ──────────────────────────────────────────────────────

// Helper: get the priority rank for a trade within its account type
function tradeRank(trade) {
    const r = trade.reasoning?.reasoning;
    const accountType = r?.account_selection?.account_type;
    const secType = (r?.model_selection?.priority_in_type || '').toLowerCase();
    const priority = PRIORITY[accountType] || [];
    const rank = priority.indexOf(secType);
    return rank === -1 ? 999 : rank;
}

/**
 * Generate a human-readable insight explaining why this trade was placed.
 * accountTrades = all trades in this account (for context-aware messages).
 */
function buildInsight(trade, accountTrades = []) {
    const r = trade.reasoning?.reasoning;
    if (!r) return null;

    const accountType = r.account_selection?.account_type;
    const secType = (r.model_selection?.priority_in_type || '').toLowerCase();
    const modelName = trade.security_model || '';
    const sleeveName = r.sleeve_selection?.sleeve_name || '';

    const priority = PRIORITY[accountType] || [];
    const rank = priority.indexOf(secType);
    const preferred = priority[0];

    // What other security types were bought in this account (excluding this trade)
    const otherTypes = accountTrades
        .filter(t => t !== trade)
        .map(t => (t.reasoning?.reasoning?.model_selection?.priority_in_type || '').toLowerCase())
        .filter(Boolean);
    const preferredAlsoBought = preferred && otherTypes.includes(preferred);

    const deviation = r.model_selection?.deviation ?? null;

    // ── Tax-optimal (first-priority type for this account) ────────────────
    if (rank === 0) {
        const acctLabel = ACCOUNT_TYPE_LABEL[accountType] || accountType;
        const tipMap = {
            non_taxable:  `${secType} are the preferred type for ${acctLabel} accounts — gains grow tax-free.`,
            tax_deferred: `${secType} are the preferred type for ${acctLabel} accounts — gains grow tax-deferred.`,
            taxable:      `${secType} are the preferred type for ${acctLabel} accounts — interest is less tax-efficient to hold outside a tax shelter.`
        };
        return {
            level: 'optimal',
            icon:  'bi-check-circle-fill',
            color: 'text-success',
            label: 'Tax-Optimal',
            text:  tipMap[accountType] || `${secType} is the highest-priority type for ${acctLabel} accounts.`
        };
    }

    // ── Second-priority ───────────────────────────────────────────────────
    if (rank === 1) {
        if (accountType === 'taxable') {
            // indexes in taxable when bonds are preferred
            return preferredAlsoBought
                ? {
                    level: 'info',
                    icon:  'bi-info-circle-fill',
                    color: 'text-primary',
                    label: 'After Maxing Bond Positions',
                    text:  `After maxing available bond positions in this account, indexes were bought from the "${modelName}" model to deploy remaining cash.`
                  }
                : {
                    level: 'info',
                    icon:  'bi-info-circle-fill',
                    color: 'text-primary',
                    label: 'Bonds Unavailable in Model',
                    text:  `Indexes were bought instead of bonds because the "${modelName}" model does not hold bond securities, or all bond positions were already at or above target.`
                  };
        }
        // non_taxable / tax_deferred — indexes when stocks are preferred
        return preferredAlsoBought
            ? {
                level: 'info',
                icon:  'bi-info-circle-fill',
                color: 'text-primary',
                label: 'After Maxing Stock Positions',
                text:  `After maxing available stock positions in this account, we used the "${modelName}" model (an index fund model) to deploy the remaining cash.`
              }
            : {
                level: 'info',
                icon:  'bi-info-circle-fill',
                color: 'text-primary',
                label: 'Index Fund Model',
                text:  `Indexes were bought because the "${modelName}" model's available securities are index funds — this model has no stock positions. Stocks are the preferred type for this account${sleeveName ? ` (${sleeveName} sleeve)` : ''}.`
              };
    }

    // ── Third-priority or beyond ──────────────────────────────────────────
    if (accountType === 'taxable' && secType === 'stocks') {
        return {
            level: 'warning',
            icon:  'bi-exclamation-triangle-fill',
            color: 'text-warning',
            label: 'Non-Preferred for Taxable',
            text:  `Stocks were placed in a taxable account because bonds and indexes in the "${modelName}" model were at or above target, or were restricted from buying. Stocks generate less tax-efficient capital gains in taxable accounts.`
        };
    }

    if ((accountType === 'non_taxable' || accountType === 'tax_deferred') && secType === 'bonds') {
        const acctLabel = ACCOUNT_TYPE_LABEL[accountType] || accountType;
        return {
            level: 'warning',
            icon:  'bi-exclamation-triangle-fill',
            color: 'text-warning',
            label: 'Non-Preferred for Tax-Advantaged',
            text:  `Bonds were placed in a ${acctLabel} account because stocks and indexes in the "${modelName}" model were at or above target, or were restricted from buying. Tax-sheltered accounts are normally best used for higher-growth assets.`
        };
    }

    // Generic fallback
    if (rank > 0 && preferred) {
        return {
            level: 'info',
            icon:  'bi-info-circle-fill',
            color: 'text-secondary',
            label: 'Fallback Security Type',
            text:  `${secType} was selected because ${preferred} securities in this model were unavailable, restricted, or already at target weight.${ deviation != null ? ` Model deviation: ${deviation.toFixed(2)}%.` : ''}`
        };
    }

    return null;
}

// ─── TradeRow ────────────────────────────────────────────────────────────────

const TradeRow = ({ trade, accountTrades, hideDollarValues }) => {
    const [expanded, setExpanded] = useState(false);
    const insight = buildInsight(trade, accountTrades);

    const secType = (
        trade.reasoning?.reasoning?.model_selection?.priority_in_type ||
        ''
    ).toLowerCase();
    const badgeColor = SEC_TYPE_BADGE[secType] || '#6c757d';

    const shares   = trade.quantity ?? 0;
    const price    = trade.price    ?? 0;
    const amount   = trade.value    ?? 0;

    return (
        <div className="border rounded mb-2 p-2" style={{ background: '#fafafa' }}>
            {/* Main trade row */}
            <div className="d-flex align-items-start gap-2 flex-wrap">
                {/* Security type badge */}
                <span
                    className="badge mt-1 flex-shrink-0"
                    style={{ background: badgeColor, minWidth: '56px', textAlign: 'center', textTransform: 'uppercase', fontSize: '0.7rem' }}
                >
                    {secType || '?'}
                </span>

                {/* Symbol + model */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-semibold">{trade.symbol}</span>
                        <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                            {shares.toLocaleString()} shares &times; {fmt(price, hideDollarValues)} = {fmt(amount, hideDollarValues)}
                        </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '1px' }}>
                        Model: {trade.security_model}
                        {trade.sleeve_name ? ` · Sleeve: ${trade.sleeve_name}` : ''}
                    </div>
                </div>

                {/* Insight badge (clickable) */}
                {insight && (
                    <button
                        className={`btn btn-sm py-0 px-2 border-0 ${insight.level === 'optimal' ? 'btn-outline-success' : insight.level === 'warning' ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
                        style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                        onClick={() => setExpanded(v => !v)}
                        title="Click to see why this trade was placed"
                    >
                        <i className={`bi ${insight.icon} me-1`}></i>
                        {insight.label}
                        <i className={`bi ms-1 ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                )}
            </div>

            {/* Expanded insight explanation */}
            {insight && expanded && (
                <div
                    className={`mt-2 p-2 rounded ${insight.level === 'optimal' ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : insight.level === 'warning' ? 'bg-warning bg-opacity-10 border border-warning border-opacity-25' : 'bg-light border'}`}
                    style={{ fontSize: '0.82rem' }}
                >
                    <i className={`bi ${insight.icon} me-1 ${insight.color}`}></i>
                    {insight.text}
                </div>
            )}
        </div>
    );
};

// ─── AccountCard ─────────────────────────────────────────────────────────────

const AccountCard = ({ accountName, accountNumber, trades, hideDollarValues }) => {
    const [collapsed, setCollapsed] = useState(false);

    // Sort: tax-optimal (rank 0) first, fallback types last
    const sortedTrades = [...trades].sort((a, b) => tradeRank(a) - tradeRank(b));

    // Pull account-level info from the first trade's reasoning
    const r0 = trades[0]?.reasoning?.reasoning?.account_selection || {};
    const accountType = r0.account_type || '';
    const currentCash    = r0.current_cash     ?? null;
    const reserveMax     = r0.reserve_max      ?? null;
    const reserveCenter  = r0.reserve_center   ?? null;
    const excessCash     = r0.excess_cash      ?? null;
    const cashInvested   = trades.reduce((s, t) => s + (t.value ?? 0), 0);

    const badgeClass = ACCOUNT_TYPE_BADGE[accountType] || 'bg-secondary';
    const acctLabel  = ACCOUNT_TYPE_LABEL[accountType] || accountType;

    return (
        <div className="card mb-3 shadow-sm">
            {/* Card header */}
            <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', background: '#f0f4ff', borderBottom: '1px solid #dde4f0' }}
                onClick={() => setCollapsed(v => !v)}
            >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'} text-muted`}></i>
                    <span className="fw-semibold">{accountName}</span>
                    <span className="text-muted" style={{ fontSize: '0.82rem' }}>({accountNumber})</span>
                    {acctLabel && (
                        <span className={`badge ${badgeClass}`} style={{ fontSize: '0.7rem' }}>
                            {acctLabel}
                        </span>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary" style={{ fontSize: '0.72rem' }}>
                        {trades.length} trade{trades.length !== 1 ? 's' : ''}
                    </span>
                    <span className="fw-semibold text-success" style={{ fontSize: '0.9rem' }}>
                        {fmtShort(cashInvested, hideDollarValues)} invested
                    </span>
                </div>
            </div>

            {!collapsed && (
                <div className="card-body py-2 px-3">
                    {/* Cash flow strip */}
                    {currentCash != null && (
                        <div className="d-flex flex-wrap gap-3 mb-3 p-2 rounded" style={{ background: '#f8f9fa', fontSize: '0.8rem' }}>
                            <div className="text-center">
                                <div className="text-muted">Current Cash</div>
                                <div className="fw-semibold">{fmt(currentCash, hideDollarValues)}</div>
                            </div>
                            <div className="d-flex align-items-center text-muted">
                                <i className="bi bi-dash"></i>
                            </div>
                            <div className="text-center">
                                <div className="text-muted">Reserve</div>
                                <div className="fw-semibold">{fmt(reserveCenter, hideDollarValues)}</div>
                            </div>
                            <div className="d-flex align-items-center text-muted">
                                <i className="bi bi-equals"></i>
                            </div>
                            <div className="text-center">
                                <div className="text-muted">Excess</div>
                                <div className={`fw-semibold ${(excessCash ?? 0) > 0 ? 'text-success' : 'text-muted'}`}>
                                    {fmt(excessCash, hideDollarValues)}
                                </div>
                            </div>
                            <div className="d-flex align-items-center text-muted">
                                <i className="bi bi-arrow-right"></i>
                            </div>
                            <div className="text-center">
                                <div className="text-muted">Deployed</div>
                                <div className="fw-semibold text-primary">{fmt(cashInvested, hideDollarValues)}</div>
                            </div>
                        </div>
                    )}

                    {/* Trades (sorted: preferred types first, fallbacks last) */}
                    {sortedTrades.map((trade, idx) => (
                        <TradeRow key={idx} trade={trade} accountTrades={sortedTrades} hideDollarValues={hideDollarValues} />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Security Type Summary Bar ────────────────────────────────────────────────

// Returns an array of per-type card elements to spread into the flex row
const SecurityTypeCards = ({ trades, hideDollarValues }) => {
    const totals = {};
    let grand = 0;

    trades.forEach(t => {
        const st = (
            t.reasoning?.reasoning?.model_selection?.priority_in_type || 'unknown'
        ).toLowerCase();
        const v = t.value ?? 0;
        totals[st] = (totals[st] || { count: 0, amount: 0 });
        totals[st].count  += 1;
        totals[st].amount += v;
        grand += v;
    });

    const entries = Object.entries(totals).sort((a, b) => b[1].amount - a[1].amount);

    return entries.map(([type, stats]) => {
        const pct = grand > 0 ? ((stats.amount / grand) * 100).toFixed(1) : '0.0';
        const color = SEC_TYPE_BADGE[type] || '#6c757d';
        return (
            <div key={type} className="card text-center flex-shrink-0" style={{ borderLeft: `3px solid ${color}`, minWidth: '130px' }}>
                <div className="card-body py-2 px-3">
                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{type}</div>
                    <div className="fw-bold" style={{ fontSize: '1.1rem', color }}>
                        {fmtShort(stats.amount, hideDollarValues)}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {stats.count} trade{stats.count !== 1 ? 's' : ''} &middot; {pct}%
                    </div>
                </div>
            </div>
        );
    });
};

// ─── Main component ───────────────────────────────────────────────────────────

const RebalancingSummaryCards = ({ rebalanceResult, hideDollarValues = false }) => {
    const trades = rebalanceResult?.trades || [];

    if (trades.length === 0) {
        return (
            <div className="text-center py-3 text-muted">
                <i className="bi bi-inbox me-2"></i>
                No trades to summarize.
            </div>
        );
    }

    // Group trades by account
    const byAccount = {};
    trades.forEach(trade => {
        const key = trade.account_number || trade.account_name || 'unknown';
        if (!byAccount[key]) {
            byAccount[key] = {
                accountName:   trade.account_name   || 'Unknown',
                accountNumber: trade.account_number || key,
                trades: []
            };
        }
        byAccount[key].trades.push(trade);
    });

    const accountGroups = Object.values(byAccount);
    const totalInvested = trades.reduce((s, t) => s + (t.value ?? 0), 0);

    return (
        <div>
            {/* ── Overview stat row + security type breakdown on same row ── */}
            <div className="d-flex align-items-stretch gap-2 mb-4 flex-wrap">
                <div className="card text-center flex-shrink-0" style={{ borderLeft: '3px solid #007bff', minWidth: '120px' }}>
                    <div className="card-body py-2 px-3">
                        <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Deployed</div>
                        <div className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                            {fmtShort(totalInvested, hideDollarValues)}
                        </div>
                    </div>
                </div>
                <div className="card text-center flex-shrink-0" style={{ borderLeft: '3px solid #28a745', minWidth: '100px' }}>
                    <div className="card-body py-2 px-3">
                        <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Trades</div>
                        <div className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>
                            {trades.length}
                        </div>
                    </div>
                </div>
                <div className="card text-center flex-shrink-0" style={{ borderLeft: '3px solid #6f42c1', minWidth: '90px' }}>
                    <div className="card-body py-2 px-3">
                        <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Accounts</div>
                        <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#6f42c1' }}>
                            {accountGroups.length}
                        </div>
                    </div>
                </div>

                {/* Per-type stat cards */}
                <SecurityTypeCards trades={trades} hideDollarValues={hideDollarValues} />
            </div>

            {/* ── Account cards ── */}
            {accountGroups.map((group) => (
                <AccountCard
                    key={group.accountNumber}
                    accountName={group.accountName}
                    accountNumber={group.accountNumber}
                    trades={group.trades}
                    hideDollarValues={hideDollarValues}
                />
            ))}

            {/* ── Logic legend ── */}
            <div className="mt-2 p-3 rounded border bg-light" style={{ fontSize: '0.78rem' }}>
                <div className="fw-semibold mb-1">
                    <i className="bi bi-lightbulb me-1 text-warning"></i>
                    How trades are prioritized
                </div>
                <div className="d-flex flex-wrap gap-3">
                    <span>
                        <span className="badge bg-success me-1">Non-Taxable &amp; Tax-Deferred</span>
                        Stocks → Indexes → Bonds → Mixed
                    </span>
                    <span>
                        <span className="badge bg-warning text-dark me-1">Taxable</span>
                        Bonds → Indexes → Stocks → Mixed
                    </span>
                </div>
                <div className="mt-1 text-muted">
                    Accounts are processed Non-Taxable → Tax-Deferred → Taxable. Within each account, the most underweight model is filled first.
                    Click any <em>insight badge</em> on a trade row to see why that security type was chosen.
                </div>
            </div>
        </div>
    );
};

export default RebalancingSummaryCards;
